import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, Typography, Box, Link, Chip } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { UserTransactionDetail } from './UserTransactionDetail';
import SCUIDownloadButton from '../../../atomic/atoms/SCUIDownloadButton/SCUIDownloadButton';
import { ExtendedGridColDef } from '@/types/ExtendedGridColDef';

interface GenerationTypeCount {
    generationTypeCode: string;
    count: number;
}

interface UserStats {
    username: string;
    totalProductions: number;
    successfulProductions: number;
    failedProductions: number;
    successRate: number;
    inpaintCount: number;
    uniqueBarcodeCount: number;
    efficiency: number;
    generationTypeBreakdown: GenerationTypeCount[];
}

interface UserDetailTableProps {
    data: UserStats[];
    startDate?: string;
    endDate?: string;
    productCode?: string;
}

const gridTextBoxStyle = {
    height: '100%',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
} as const;

export const UserDetailTable: FC<UserDetailTableProps> = ({ data, startDate, endDate, productCode }) => {
    const { t } = useTranslation();
    const [selectedUsername, setSelectedUsername] = useState<string | null>(null);

    // Tüm kullanıcılardan benzersiz üretim tiplerini çıkar
    const allGenerationTypes = useMemo(() => {
        const typeSet = new Set<string>();
        data?.forEach((user) => {
            user.generationTypeBreakdown?.forEach((item) => {
                if (item.generationTypeCode) {
                    typeSet.add(item.generationTypeCode);
                }
            });
        });
        return Array.from(typeSet).sort((a, b) => a.localeCompare(b));
    }, [data]);

    // Veri kontrolü
    if (!data || data.length === 0) {
        return (
            <Card elevation={0} sx={{ borderRadius: '12px', border: '1px solid', borderColor: 'divider' }}>
                <CardContent sx={{ p: '24px !important' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 3, color: 'text.primary' }}>
                        {t('aiStudioAnalytics.userDetails')}
                    </Typography>
                    <Box sx={{ height: 500, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Typography color="textSecondary">{t('aiStudioAnalytics.noData')}</Typography>
                    </Box>
                </CardContent>
            </Card>
        );
    }

    // Dinamik üretim tipi kolonları oluştur
    const generationTypeColumns: ExtendedGridColDef[] = allGenerationTypes.map((typeCode) => ({
        field: `genType_${typeCode}`,
        headerName: typeCode,
        flex: 1,
        minWidth: 100,
        align: 'center' as const,
        headerAlign: 'center' as const,
        renderCell: (params: any) => {
            const value = params.value || 0;
            return value > 0 ? (
                <Chip
                    label={value}
                    size="small"
                    color="primary"
                    variant="outlined"
                    sx={{ height: 24, fontSize: '0.8rem', fontWeight: 600 }}
                />
            ) : (
                <Box sx={gridTextBoxStyle}>
                    <Typography variant="body2" color="textSecondary">
                        0
                    </Typography>
                </Box>
            );
        },
    }));

    const columns: ExtendedGridColDef[] = [
        {
            field: 'username',
            headerName: t('aiStudioAnalytics.username'),
            flex: 1,
            minWidth: 150,
            renderCell: (params) => (
                <Link
                    component="button"
                    variant="body2"
                    onClick={() => setSelectedUsername(params.value)}
                    sx={{
                        cursor: 'pointer',
                        textDecoration: 'none',
                        '&:hover': {
                            textDecoration: 'underline',
                        },
                    }}
                >
                    {params.value}
                </Link>
            ),
        },
        {
            field: 'totalProductions',
            headerName: t('aiStudioAnalytics.totalProductions'),
            flex: 1,
            minWidth: 120,
            align: 'center',
            headerAlign: 'center',
        },
        ...generationTypeColumns,
        {
            field: 'inpaintCount',
            headerName: t('aiStudioAnalytics.inpaintCount'),
            flex: 1,
            minWidth: 100,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params: any) => {
                const value = params.value || 0;
                return value > 0 ? (
                    <Chip
                        label={value}
                        size="small"
                        color="secondary"
                        variant="outlined"
                        sx={{ height: 24, fontSize: '0.8rem', fontWeight: 600 }}
                    />
                ) : (
                    <Box sx={gridTextBoxStyle}>
                        <Typography variant="body2" color="textSecondary">
                            0
                        </Typography>
                    </Box>
                );
            },
        },
        {
            field: 'uniqueBarcodeCount',
            headerName: t('aiStudioAnalytics.uniqueBarcodeCount'),
            flex: 1,
            minWidth: 120,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params: any) => {
                const value = params.value || 0;
                return value > 0 ? (
                    <Chip
                        label={value}
                        size="small"
                        color="info"
                        variant="outlined"
                        sx={{ height: 24, fontSize: '0.8rem', fontWeight: 600 }}
                    />
                ) : (
                    <Box sx={gridTextBoxStyle}>
                        <Typography variant="body2" color="textSecondary">
                            0
                        </Typography>
                    </Box>
                );
            },
        },
        {
            field: 'efficiency',
            headerName: t('aiStudioAnalytics.efficiency'),
            flex: 1,
            minWidth: 150,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params: any) => {
                const row = params.row;
                const uniqueBarcode = row.uniqueBarcodeCount || 0;
                const successful = row.successfulProductions || 0;
                if (uniqueBarcode === 0 || successful === 0) {
                    return (
                        <Box sx={gridTextBoxStyle}>
                            <Typography variant="body2" color="textSecondary">
                                -
                            </Typography>
                        </Box>
                    );
                }
                const avgPerBarcode = (successful / uniqueBarcode).toFixed(1);
                const percent = Math.min(Math.round((uniqueBarcode / successful) * 100), 100);
                const color = percent >= 70 ? '#2e7d32' : percent >= 40 ? '#ed6c02' : '#d32f2f';
                return (
                    <Box sx={{ ...gridTextBoxStyle, gap: 0.5 }}>
                        <Chip
                            label={`%${percent}`}
                            size="small"
                            sx={{
                                height: 24,
                                fontSize: '0.8rem',
                                fontWeight: 700,
                                color: '#fff',
                                backgroundColor: color,
                                borderColor: color,
                            }}
                        />
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                            ({avgPerBarcode})
                        </Typography>
                    </Box>
                );
            },
        },
        {
            field: 'successfulProductions',
            headerName: t('aiStudioAnalytics.successful'),
            flex: 1,
            minWidth: 100,
            align: 'center',
            headerAlign: 'center',
        },
        {
            field: 'failedProductions',
            headerName: t('aiStudioAnalytics.failed'),
            flex: 1,
            minWidth: 100,
            align: 'center',
            headerAlign: 'center',
        },
        {
            field: 'successRate',
            headerName: t('aiStudioAnalytics.successRate'),
            flex: 1,
            minWidth: 110,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => `${(params.value ?? 0).toFixed(1)}%`,
        },
    ];

    // Veri zaten camelCase formatında, sadece id ekle + dinamik genType alanları ekle
    const rows = data.map((user, index) => {
        const genTypeFields: Record<string, number> = {};
        allGenerationTypes.forEach((typeCode) => {
            const found = user.generationTypeBreakdown?.find((item) => item.generationTypeCode === typeCode);
            genTypeFields[`genType_${typeCode}`] = found ? found.count : 0;
        });
        return {
            id: index,
            ...user,
            ...genTypeFields,
        };
    });

    // Eğer bir kullanıcı seçilmişse, transaction detaylarını göster
    if (selectedUsername) {
        return (
            <Box>
                <Box sx={{ mb: 2 }}>
                    <Link
                        component="button"
                        variant="body2"
                        onClick={() => setSelectedUsername(null)}
                        sx={{ fontSize: '1rem', fontWeight: 'bold' }}
                    >
                        ← {t('aiStudioAnalytics.backToUserList')}
                    </Link>
                </Box>
                <UserTransactionDetail username={selectedUsername} startDate={startDate} endDate={endDate} productCode={productCode} />
            </Box>
        );
    }

    return (
        <Card elevation={0} sx={{ borderRadius: '12px', border: '1px solid', borderColor: 'divider' }}>
            <CardContent sx={{ p: '24px !important' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary' }}>
                        {t('aiStudioAnalytics.userDetails')}
                    </Typography>
                    <SCUIDownloadButton
                        position="right"
                        containerStyle={{ width: 'auto' }}
                        excelData={{
                            excelDownloadData: data.map((user) => {
                                const genTypeData: Record<string, number> = {};
                                allGenerationTypes.forEach((typeCode) => {
                                    const found = user.generationTypeBreakdown?.find(
                                        (item) => item.generationTypeCode === typeCode
                                    );
                                    genTypeData[typeCode] = found ? found.count : 0;
                                });
                                return {
                                    username: user.username,
                                    totalProductions: user.totalProductions,
                                    ...genTypeData,
                                    inpaintCount: user.inpaintCount || 0,
                                    uniqueBarcodeCount: user.uniqueBarcodeCount || 0,
                                    efficiency:
                                        user.uniqueBarcodeCount && user.successfulProductions
                                            ? `%${Math.min(Math.round((user.uniqueBarcodeCount / user.successfulProductions) * 100), 100)} (${(user.successfulProductions / user.uniqueBarcodeCount).toFixed(1)})`
                                            : '-',
                                    successfulProductions: user.successfulProductions,
                                    failedProductions: user.failedProductions,
                                    successRate: user.successRate ? `${user.successRate.toFixed(2)}%` : '0%',
                                };
                            }),
                            excelDownloadFolderName: t('aiStudioAnalytics.userDetails'),
                            excelDownloadButtonText: t('general.exportToExcel'),
                        }}
                    />
                </Box>
                <div style={{ height: 600, width: '100%' }}>
                    <DataGrid
                        rows={rows}
                        columns={columns}
                        paginationModel={{ page: 0, pageSize: 50 }}
                        disableRowSelectionOnClick
                        sx={{
                            border: 'none',
                            '& .MuiDataGrid-columnHeaders': {
                                backgroundColor: '#f8fafc',
                                borderBottom: '1px solid',
                                borderColor: 'divider',
                            },
                            '& .MuiDataGrid-cell': {
                                borderBottom: '1px solid',
                                borderColor: 'slate.100',
                            },
                            '& .MuiDataGrid-footerContainer': {
                                borderTop: '1px solid',
                                borderColor: 'divider',
                            },
                        }}
                    />
                </div>
            </CardContent>
        </Card>
    );
};
